import { useState } from 'react';
import { Footer, Header, Link } from '../components/Brand.tsx';
import { cleanHandleInput, useHandleCheck } from '../lib/handle-check.ts';
import { navigate } from '../lib/router.ts';
import { FAQS } from './Info.tsx';

const STEPS = [
  ['01', 'Go live', 'Open ZIP2LIVE on your phone or computer and tap GO LIVE. Camera, screen or OBS.'],
  ['02', 'Share', 'Your link is ready right away, before any video flows. Drop it in X, Discord, iMessage, anywhere.'],
  ['03', 'They watch', 'Anyone with the link watches in their browser, a couple of seconds behind you. Nothing to install.'],
] as const;

export function Home() {
  return (
    <div className="flex min-h-full flex-col">
      <Header />
      <main className="flex-1">
        <section className="mx-auto flex max-w-6xl flex-wrap items-center gap-14 px-5 pt-6 pb-16 sm:px-8 sm:pt-12 sm:pb-24">
          <div className="min-w-0 flex-[999_1_520px]">
            <div className="flex items-center gap-3">
              <span className="speed-stripes" aria-hidden />
              <span className="text-xs font-extrabold tracking-[0.14em] text-muted sm:text-[13px]">NO APP · NO SIGNUP · NO SETUP</span>
            </div>
            <h1 className="speed-text mt-5 text-[clamp(3rem,9vw,5.75rem)] leading-[0.92]">
              Live in seconds.
              <br />
              <span className="text-race">Share anywhere.</span>
            </h1>
            <p className="mt-6 max-w-lg text-lg leading-relaxed text-muted sm:text-xl">
              Tap once and your link is ready before your camera is. Drop it anywhere. Anyone with it watches in their browser.
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-x-8 gap-y-5">
              <button type="button" onClick={() => navigate('/go-live')} className="btn-race shadow-race-navy h-[76px] w-full px-11 text-[32px] sm:w-auto sm:text-[34px]">
                <span className="h-3.5 w-3.5 rounded-full bg-white" aria-hidden />
                GO LIVE
              </button>
              <Link to="/obs" className="mx-auto font-bold underline-offset-4 hover:text-race hover:underline sm:mx-0">
                Using OBS? Get your server and key →
              </Link>
            </div>
          </div>
          <PhonePreview />
        </section>

        <section className="bg-navy text-white">
          <div className="mx-auto max-w-6xl px-5 py-16 sm:px-8 sm:py-22">
            <h2 className="speed-text text-4xl sm:text-5xl">
              Three steps. <span className="text-race-300">That’s the whole thing.</span>
            </h2>
            <ol className="mt-10 grid gap-5 sm:grid-cols-3">
              {STEPS.map(([n, t, d]) => (
                <li key={n} className="rounded-2xl border-2 border-line-dark p-7">
                  <span className="speed-text text-6xl leading-none text-race" aria-hidden>{n}</span>
                  <h3 className="speed-text mt-4 text-2xl">{t}</h3>
                  <p className="mt-2 leading-relaxed text-muted-dark">{d}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <MakeItYours />
        <section className="bg-paper">
          <div className="mx-auto max-w-6xl px-5 py-16 sm:px-8 sm:py-20">
            <h2 className="speed-text text-4xl sm:text-5xl">
              Questions<span className="text-race">?</span>
            </h2>
            <dl className="mt-8 grid gap-4 sm:grid-cols-2">
              {FAQS.slice(0, 4).map(([q, a]) => (
                <div key={q} className="rounded-3xl border border-line bg-white p-6">
                  <dt className="text-lg font-extrabold">{q}</dt>
                  <dd className="mt-2 leading-relaxed text-muted">{a}</dd>
                </div>
              ))}
            </dl>
            <Link to="/faq" className="mt-6 inline-block font-bold underline-offset-4 hover:text-race hover:underline">
              All questions →
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}

/**
 * "Make it yours later": type a name and see straight away whether zip2live.com/<name> is free.
 * Claiming goes through the usual sign-in with the name filled in; nothing is held until then.
 */
function MakeItYours() {
  const [handle, setHandle] = useState('');
  const status = useHandleCheck(handle);
  const claim = status.kind === 'ok' ? `/claim?handle=${encodeURIComponent(status.handle)}` : '/claim';
  return (
    <section className="mx-auto flex max-w-6xl flex-wrap items-center gap-12 px-5 py-16 sm:px-8 sm:py-24">
      <div className="min-w-0 flex-[1_1_380px]">
        <h2 className="speed-text text-5xl leading-[0.95] sm:text-6xl">
          Make it yours
          <br />
          <span className="text-race">later.</span>
        </h2>
        <p className="mt-5 max-w-md text-lg leading-relaxed text-muted">
          Stream as a guest first. When you want to keep your streams and use them on other devices, sign in with a one-time code to your email or phone. No password, ever.
        </p>
        <Link to={claim} className="mt-6 inline-block font-bold underline-offset-4 hover:text-race hover:underline">
          Claim your link →
        </Link>
      </div>
      <form
        noValidate
        className="min-w-0 flex-[1_1_380px] rounded-3xl bg-mist p-6 sm:p-8"
        onSubmit={(e) => {
          e.preventDefault();
          if (status.kind === 'ok') navigate(claim);
        }}
      >
        <label htmlFor="home-handle" className="font-extrabold">Your personal link</label>
        <div className={`mt-3 flex h-15 items-center overflow-hidden rounded-2xl border-2 bg-white px-4 text-lg sm:text-xl ${status.kind === 'ok' ? 'border-ok' : status.kind === 'bad' ? 'border-race' : 'border-navy'}`}>
          <span className="text-muted">zip2live.com/</span>
          <input id="home-handle" value={handle} placeholder="yourname" maxLength={24} autoComplete="off" autoCapitalize="off" autoCorrect="off" spellCheck={false}
            enterKeyHint="go" aria-describedby="home-handle-status" aria-invalid={status.kind === 'bad'}
            onChange={(e) => setHandle(cleanHandleInput(e.target.value))}
            className="h-full min-w-0 flex-1 font-extrabold text-navy outline-none placeholder:text-navy/35" />
          <span aria-hidden className="ml-2 inline-flex w-6 shrink-0 justify-center">
            {status.kind === 'checking' && <span className="h-5 w-5 animate-spin rounded-full border-2 border-line border-t-navy" />}
            {status.kind === 'ok' && <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--color-ok)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12l5 5L20 7" /></svg>}
            {status.kind === 'bad' && <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--color-race)" strokeWidth="3" strokeLinecap="round"><path d="M6 6l12 12M18 6L6 18" /></svg>}
          </span>
        </div>
        <p id="home-handle-status" role="status" aria-live="polite" className={`mt-2.5 min-h-5 text-sm font-bold ${status.kind === 'ok' ? 'text-ok' : status.kind === 'bad' ? 'text-race-600' : 'text-muted'}`}>
          {status.kind === 'checking' ? 'Checking…' : status.kind === 'ok' ? `zip2live.com/${status.handle} is available.` : status.kind === 'bad' ? status.message : 'Type a name to see if it’s free.'}
        </p>
        {status.kind === 'ok' && (
          <Link to={claim} className="btn-race mt-4 min-h-14 w-full px-5 py-3 text-center text-base leading-tight text-balance sm:text-lg">
            Claim before someone else takes it&nbsp;→
          </Link>
        )}
        <p className="mt-4 leading-relaxed text-muted">Whenever you’re live, that link plays your stream. When you’re not, it says you’re offline.</p>
      </form>
    </section>
  );
}

/** Illustration of the live screen: what a creator sees seconds after tapping GO LIVE. */
function PhonePreview() {
  return (
    <div className="hidden flex-[1_1_320px] justify-center md:flex" aria-hidden>
      <div className="h-[620px] w-[310px] rounded-[48px] bg-navy-900 p-3 shadow-[0_30px_60px_rgba(11,36,84,0.25)]">
        <div className="relative h-full w-full overflow-hidden rounded-[38px] bg-[#1b2c52]">
          <div className="absolute top-[120px] -left-10 h-7 w-[400px] -skew-x-[30deg] bg-[#24386a]" />
          <div className="absolute top-[170px] left-16 h-2.5 w-[300px] -skew-x-[30deg] bg-race/80" />
          <div className="absolute top-[200px] -left-5 h-3.5 w-[260px] -skew-x-[30deg] bg-[#2e4680]" />
          <div className="absolute top-5 left-4 flex items-center gap-2">
            <span className="speed-text inline-flex items-center gap-1.5 bg-race px-2.5 py-1 text-xs text-white">
              <span className="h-1.5 w-1.5 rounded-full bg-white" />
              On air
            </span>
          </div>
          <div className="absolute inset-x-2.5 bottom-2.5 rounded-[26px] bg-white p-4">
            <p className="text-[15px] font-extrabold">You’re live. Share your link</p>
            <div className="mt-2.5 rounded-xl bg-mist px-3.5 py-3 font-mono text-sm">zip2live.com/s/8ussdvtx</div>
            <div className="mt-2.5 grid grid-cols-2 gap-2 text-center text-sm font-extrabold">
              <span className="rounded-xl border-2 border-line py-2.5">Copy link</span>
              <span className="rounded-xl bg-navy py-2.5 text-white">Share…</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
